import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";
import imageemi from "../assets/emicalculator.webp";
import { LazyImageComponent } from "../components/lazyloader/LazyImage";

const Emi = () => {
  const minAmount = 50000;
  const maxAmount = 500000;

  const [loanType, setLoanType] = useState("home");
  const [amountPercentage, setAmountPercentage] = useState(0);
  const [loanAmount, setLoanAmount] = useState(minAmount);
  const [loanDuration, setLoanDuration] = useState(12);
  const [durationType, setDurationType] = useState("months");
  const [interestRate, setInterestRate] = useState(10);
  const [emi, setEmi] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);

  const amountSliderRef = useRef(null);
  const interestSliderRef = useRef(null);

  const calculatePercentage = (amount) =>
    Math.round(((amount - minAmount) / (maxAmount - minAmount)) * 100);

  const calculateAmount = (percentage) =>
    Math.round(minAmount + (percentage / 100) * (maxAmount - minAmount));

  const handlePercentageChange = (e) => {
    const newPercentage = Number(e.target.value);
    setAmountPercentage(newPercentage);
    setLoanAmount(calculateAmount(newPercentage));
  };

  useEffect(() => {
    setAmountPercentage(calculatePercentage(loanAmount));
  }, [loanAmount]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (amountSliderRef.current) {
      const percentage = amountPercentage;

      amountSliderRef.current.style.background = `
      linear-gradient(to right, #F08100 ${percentage}%, #F08100 ${percentage}%)
    `;
    }
  }, [amountPercentage]);

  useEffect(() => {
    if (interestSliderRef.current) {
      const percentage = ((interestRate - 10) / 20) * 100;
      //  interestSliderRef.current.style.background = ` linear-gradient(to right, #F08100 ${percentage}%, #F08100 ${percentage}%)`;
    }
  }, [interestRate]);

  const calculateEMI = () => {
    const monthlyInterestRate = interestRate / 1200;
    const totalMonths =
      durationType === "years" ? loanDuration * 12 : loanDuration;
    const emiValue =
      (loanAmount *
        monthlyInterestRate *
        Math.pow(1 + monthlyInterestRate, totalMonths)) /
      (Math.pow(1 + monthlyInterestRate, totalMonths) - 1);

    const totalPayment = emiValue * totalMonths;
    const totalInterestPayment = totalPayment - loanAmount;

    setEmi(emiValue || 0);
    setTotalAmount(totalPayment || 0);
    setTotalInterest(totalInterestPayment || 0);
  };

  useEffect(() => {
    calculateEMI();
  }, [loanAmount, loanDuration, interestRate, durationType]);

  const pieData = [
    { name: "Principal Amount", value: totalAmount - totalInterest },
    { name: "Total Interest", value: totalInterest },
  ];

  const COLORS = ["#FF8C8B", "#8B96FF"];

  return (
    <>
      <div className=" py-5" style={{ backgroundColor: "#FFEFCA" }}>
        <div className="row">
          <div className="d-none d-md-block col-md-1" />

          <div className="col-12 col-md-10">
            <div className="p-0 rounded-3">
              <div className="row justify-content-center">
                <h4
                  className="text-center fw-semibold mb-5"
                  style={{ fontSize: "32px" }}
                >
                  EMI Calculator
                </h4>

                <div className="row ">
                  {/* Left */}
                  <div className="col-12 col-md-6">
                    <div className="border-0 p-2 ">
                      <div className="mb-3">
                        <label className="form-label fw-medium text-secondary">
                          Loan Type
                        </label>
                        <select
                          className="form-select"
                          value={loanType}
                          onChange={(e) => setLoanType(e.target.value)}
                        >
                          <option value="home">Housing Loan</option>
                          <option value="personal">Personal Loan</option>
                          <option value="car">Vehicle Loan</option>
                          <option value="property">
                            Loan Against Property
                          </option>
                          <option value="gold">Gold Loan</option>
                          <option value="business">Business Loan</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-medium text-secondary">
                          Loan Amount (₹)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={loanAmount}
                          onChange={(e) =>
                            setLoanAmount(Number(e.target.value))
                          }
                        />
                        <div className="mt-2">
                          <input
                            type="range"
                            min="50000"
                            max="500000"
                            step="10000"
                            value={loanAmount}
                            onChange={(e) =>
                              setLoanAmount(Number(e.target.value))
                            }
                            className="w-100"
                          />

                          <div className="d-flex justify-content-between small text-primary mt-1">
                            <span>₹{minAmount.toLocaleString()}</span>
                            <span>₹{maxAmount.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-medium text-secondary">
                          Loan Duration
                        </label>
                        <div className="d-flex">
                          <input
                            type="number"
                            className="form-control"
                            value={loanDuration}
                            onChange={(e) =>
                              setLoanDuration(Number(e.target.value))
                            }
                          />
                          <div
                            className="ms-2 d-flex align-items-center rounded-pill bg-white"
                            style={{
                              overflow: "hidden",
                              width: "130px",
                              height: "38px",
                            }}
                          >
                            <div
                              className="position-absolute"
                              style={{
                                width: "50%",
                                height: "70%",
                                top: "15%",
                                left: durationType === "months" ? "0%" : "50%",
                                transition: "left 0.25s ease",
                                zIndex: 0,
                                pointerEvents: "none",
                                borderRadius: "20px",
                              }}
                            />

                            <button
                              type="button"
                              a
                              className={`btn btn-sm w-50 border-0 ${
                                durationType === "months"
                                  ? "text-primary"
                                  : "text-gray"
                              }`}
                              onClick={() => setDurationType("months")}
                              style={{ background: "transparent", zIndex: 1 }}
                            >
                              Mo
                            </button>

                            <button
                              type="button"
                              className={`btn btn-sm w-50 border-0 ${
                                durationType === "years"
                                  ? "text-primary"
                                  : "text-gray"
                              }`}
                              onClick={() => setDurationType("years")}
                              style={{ background: "transparent", zIndex: 1 }}
                            >
                              Yr
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-medium text-secondary">
                          Rate of Interest
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={interestRate}
                          step="0.1"
                          onChange={(e) =>
                            setInterestRate(Number(e.target.value))
                          }
                        />
                        <div className="d-flex justify-content-between small text-primary mt-2">
                          <span>10% p.a.</span>
                          <span>30% p.a.</span>
                        </div>
                        <input
                          type="range"
                          min="5"
                          max="20"
                          step="0.5"
                          value={interestRate}
                          onChange={(e) =>
                            setInterestRate(Number(e.target.value))
                          }
                          className="w-100 "
                        />
                      </div>

                      <div className="mt-3">
                        <Link
                          to="/"
                          className="btn text-white fw-semibold"
                          style={{ textDecoration: "none", backgroundColor: "#2353A6" }}
                        >
                          Apply Now <i className="fas fa-arrow-right ms-2" />
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Right */}
                  <div className="col-12 col-md-6 mt-2 mt-md-5">
                    <div
                      className="position-relative"
                      style={{ minHeight: "420px" }}
                    >
                   
                      <div
                        className="position-absolute end-0 top-0 h-100 d-none d-sm-flex align-items-center justify-content-end"
                        style={{ width: "100%" }}
                      >
                        <LazyImageComponent
                          src={imageemi}
                          alt="EMI Illustration"
                          className="img-fluid"
                          style={{
                            width: "65%",
                            maxHeight: "550px",
                            objectFit: "cover",
                            borderRadius: "20px",
                          }}
                        />
                      </div>
                      
                      <div
                        className="position-absolute emi-card"
                        style={{
                          top: "50%",
                          left: "80px",
                          transform: "translateY(-50%)",
                          width: "85%",
                          maxWidth: "400px",
                          height: "100%",
                          background: "white",
                          backdropFilter: "blur(12px)",
                          borderRadius: "20px",
                          padding: "20px",
                        }}
                      >
                        <div className="d-flex justify-content-center mb-3">
                          <PieChart width={160} height={160}>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={70}
                              dataKey="value"
                            >
                              {pieData.map((entry, index) => (
                                <Cell
                                  key={index}
                                  fill={COLORS[index]}
                                  stroke="#fff"
                                  strokeWidth={2}
                                />
                              ))}
                            </Pie>
                          </PieChart>
                        </div>

                        <div
                          className="card border-0 p-2 mb-3"
                          style={{ backgroundColor: "#FAF9FF" }}
                        >
                          <div className="row text-center mb-3">
                            <div className="col-6">
                              <p className="small mb-1 mt-2">Total Amount</p>
                              <span
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "50%",
                                  backgroundColor: "#FF8C8B",
                                  display: "inline-block",
                                  marginRight: "6px",
                                }}
                              ></span>
                              <strong>₹{totalAmount.toFixed(2)}</strong>
                            </div>
                            <div className="col-6 ">
                              <p className="small mb-1 mt-2">Total Interest</p>
                              <span
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "50%",
                                  backgroundColor: "#8B96FF",
                                  display: "inline-block",
                                  marginRight: "6px",
                                }}
                              ></span>
                              <strong>₹{totalInterest.toFixed(2)}</strong>
                            </div>
                          </div>
                        </div>

                        <button
                          className="btn w-100 fw-bold text-center"
                          style={{
                            background: "#FF8D00",
                            color: "#fff",
                            borderRadius: "15px",
                            fontSize: "1.1rem",
                            padding: "10px",
                            lineHeight: "1.4",
                          }}
                        >
                          <span style={{ display: "block" }}>Monthly EMI</span>
                          <span
                            style={{ display: "block", fontSize: "1.3rem" }}
                          >
                            ₹{emi.toFixed(2)}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="d-none d-md-block col-md-1" />
        </div>
      </div>
    </>
  );
};

export default Emi;  